import React, { useMemo } from "react";
import { useProjectSource } from "../../../../../../../../api/useSources";
import { isNonEmptyArray } from "diagnostics-lib/array";
import { css } from "@linaria/core";
import BlankStateDiagnosis from "../../../../../../Diagnosis/FailureEvolution/components/BlankStateDiagnosis";
import useSelectedAnalyses from "../../../../../../Analysis/useSelectedAnalyses";
import { useSetups } from "../../../../../../../../api/useSetups";
import { useIndexedFailureValues } from "../../../../../../../../api/useIndexedFailureValues";
import { useIndexedManualFailureValues } from "../../../../../../../../api/useManualFailureValues";
import useDebounce from "@ui/lib/hooks/useDebounce";
import { spacings } from "@ui/Theme";
import { useIndexedFailureMetadata } from "../../../../../../../../api/useFailureMetadata";
import DiagnosisSymptomAndImpacts from "./Symptoms";
import FailureEvolution from "./FailureEvolution";
import { useDisplayAllFaults } from "./useDisplayAllFaults";
import { useQuantities } from "../../../../../../../../api/useQuantities";
import { useOutletContext } from "react-router";
import type { EventOutletContext } from "../../..";
import { getEventScope } from "../../../../../../../../domain/event";
import BlankStateAnyIndicatorPerfomance from "./BlankStateAnyIndicatorPerfomance";
import useActiveAnalysesWithDisplayFilters from "../../../../../../../../api/useActiveAnalysesWithDisplayFilters";

export const DiagnosisTab = () => {
  const { project, event, onCloseEvent } = useOutletContext<EventOutletContext>();
  if (!project) throw new Error("no project");
  if (!event) throw new Error("no event");

  const source = useProjectSource(project);
  const { displayFilterRemovesAllActiveAnalyses, activeAnalyses } =
    useActiveAnalysesWithDisplayFilters(project.id);
  const quantities = useQuantities(project.id);
  const indicatorPerformanceQuantities = useMemo(
    () =>
      quantities
        .filter(q => q.qoiOrder !== null)
        .sort((a, b) => (a.qoiOrder ?? 0) - (b.qoiOrder ?? 0)),
    [quantities],
  );
  const setups = useSetups(project.id);

  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);

  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalyses);
  const debouncedSelectedAnalyses = useDebounce(selectedAnalyses, 300);

  const eventScope = getEventScope(event);
  const [displayAllFaults, _] = useDisplayAllFaults({
    scope: eventScope,
  });

  if ((!isNonEmptyArray(activeAnalyses) && !displayFilterRemovesAllActiveAnalyses) || !source)
    return <BlankStateDiagnosis project={project} />;

  if (indicatorPerformanceQuantities.length === 0)
    return (
      <div className={blankStateContainer}>
        <BlankStateAnyIndicatorPerfomance project={project} />
      </div>
    );

  return (
    <div className={diagnosisTabContainer}>
      <FailureEvolution
        eventScope={eventScope}
        event={event}
        project={project}
        quantities={quantities}
        setups={setups}
        activeAnalyses={activeAnalyses}
        indexedFailureValues={indexedFailureValues}
        indexedFailureMetadata={indexedFailureMetadata}
        indexedManualFailureValues={indexedManualFailureValues}
        source={source}
        onCloseEvent={onCloseEvent}
      />
      {debouncedSelectedAnalyses[0] && debouncedSelectedAnalyses.length === 1 ? (
        <DiagnosisSymptomAndImpacts
          faultEventScope={eventScope}
          event={event}
          visibleSymptomHistogramChartId={`${eventScope}-${debouncedSelectedAnalyses[0].id}-symptom-histogram-${displayAllFaults}`}
          project={project}
          quantities={quantities}
          setups={setups}
          analysis={debouncedSelectedAnalyses[0]}
          indexedFailureMetadata={indexedFailureMetadata}
          indexedFailureValues={indexedFailureValues}
          indexedManualFailureValues={indexedManualFailureValues}
        />
      ) : null}
    </div>
  );
};

//#region CSS
const diagnosisTabContainer = css`
  margin: 24px 48px;
  height: fit-content;

  & > *:not(:last-child) {
    margin-bottom: ${spacings.xl};
  }
`;
const blankStateContainer = css`
  height: max-content;
`;
//#endregion CSS
